import React, { useEffect, useState } from 'react';
import { Calendar, TrendingUp, Award, Home, Baby, Briefcase, GraduationCap } from 'lucide-react';
import { fetchLifeEventsData, initializeCustomerId } from '../api';

const DEFAULT_EVENTS = [
    {
        event_id: 'default_1',
        event_type: 'retirement',
        title: 'Retirement Planning Milestone',
        date: new Date(new Date().setFullYear(new Date().getFullYear() + 3)).toISOString(),
        impact: 'Review retirement portfolio allocation and projected income streams',
        recommended_action: 'Schedule retirement strategy review',
        status: 'upcoming'
    },
    {
        event_id: 'default_2',
        event_type: 'college education',
        title: 'Education Fund Review',
        date: new Date(new Date().setMonth(new Date().getMonth() + 8)).toISOString(),
        impact: 'Assess 529 plan performance and contribution strategy for upcoming tuition',
        recommended_action: 'Review education savings plan',
        status: 'upcoming'
    },
    {
        event_id: 'default_3',
        event_type: 'anniversary',
        title: 'Annual Portfolio Review',
        date: new Date(new Date().setMonth(new Date().getMonth() + 2)).toISOString(),
        impact: 'Year-end tax optimization and rebalancing opportunity',
        recommended_action: 'Prepare year-end tax strategy',
        status: 'upcoming'
    }
];

const LifeEventsWidget = ({ userId, useCaseId }) => {
    const [events, setEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const getEventIcon = (eventType) => {
        // Map database milestone types to icons
        const normalizedType = eventType?.toLowerCase() || '';
        
        if (normalizedType.includes('birthday') || normalizedType.includes('anniversary')) {
            return <TrendingUp size={20} className="text-emerald-400" />;
        } else if (normalizedType.includes('college') || normalizedType.includes('education')) {
            return <GraduationCap size={20} className="text-emerald-400" />;
        } else if (normalizedType.includes('home') || normalizedType.includes('house')) {
            return <Home size={20} className="text-emerald-400" />;
        } else if (normalizedType.includes('retirement')) {
            return <Award size={20} className="text-emerald-400" />;
        } else if (normalizedType.includes('child') || normalizedType.includes('baby')) {
            return <Baby size={20} className="text-emerald-300" />;
        } else if (normalizedType.includes('promotion') || normalizedType.includes('career')) {
            return <Briefcase size={20} className="text-emerald-400" />;
        }
        return <Calendar size={20} className="text-slate-400" />;
    };

    const getEventColor = (eventType) => {
        // Map database milestone types to colors - simplified to match theme
        const normalizedType = eventType?.toLowerCase() || '';
        
        if (normalizedType.includes('college') || normalizedType.includes('education') || 
            normalizedType.includes('child') || normalizedType.includes('baby')) {
            return 'border-primary';
        }
        // Default to emerald for most events
        return 'border-primary';
    };

    useEffect(() => {
        const fetchLifeEvents = async () => {
            if (!userId) return;
            
            try {
                setIsLoading(true);
                
                // Initialize customer ID first to avoid race conditions
                await initializeCustomerId(userId);
                
                // Fetch life events from database via SQL agent
                const milestonesData = await fetchLifeEventsData(userId, useCaseId);
                
                if (Array.isArray(milestonesData) && milestonesData.length > 0) {
                    // Transform database format to component format
                    const transformedEvents = milestonesData.slice(0, 3).map(milestone => ({
                        event_id: milestone.milestone_id || `event_${Date.now()}_${Math.random()}`,
                        event_type: milestone.milestone || 'general',
                        title: milestone.milestone || 'Upcoming milestone',
                        date: milestone.reminder_date || new Date().toISOString(),
                        impact: milestone.note || 'Important milestone approaching',
                        recommended_action: milestone.note || 'Review details',
                        status: 'upcoming'
                    }));

                    setEvents(transformedEvents);
                } else {
                    setEvents(DEFAULT_EVENTS);
                }
            } catch (error) {
                console.error('Error fetching life events:', error);
                setEvents(DEFAULT_EVENTS);
            } finally {
                setIsLoading(false);
            }
        };

        fetchLifeEvents();
    }, [userId, useCaseId]);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = date - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays < 0) {
            return `${Math.abs(diffDays)} days ago`;
        } else if (diffDays < 30) {
            return `In ${diffDays} days`;
        } else if (diffDays < 365) {
            const months = Math.ceil(diffDays / 30);
            return `In ${months} ${months === 1 ? 'month' : 'months'}`;
        } else {
            const years = Math.ceil(diffDays / 365);
            return `In ${years} ${years === 1 ? 'year' : 'years'}`;
        }
    };

    return (
        <div
            className="bg-gradient-to-br from-slate-900/80 to-slate-800/80 backdrop-blur-md border border-slate-700/50 rounded-lg relative overflow-hidden h-full"
        >
            {/* Decorative elements */}
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-emerald-500/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-emerald-500/8 rounded-full blur-3xl"></div>

            {/* Content */}
            <div className="relative z-10 p-4 h-full flex flex-col">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Calendar size={20} className="text-emerald-400" />
                        <h2 className="text-lg font-medium text-slate-100">Life Events & Milestones</h2>
                    </div>
                    <span className="text-xs text-slate-400">Personalized Timeline</span>
                </div>

                {isLoading ? (
                    <div className="animate-pulse space-y-3 flex-1">
                        {[...Array(3)].map((_, i) => (
                            <div key={i} className="h-24 bg-slate-700/50 rounded-lg"></div>
                        ))}
                    </div>
                ) : events.length === 0 ? (
                    <div className="flex-1 flex items-center justify-center">
                        <div className="text-center text-slate-400">
                            <Calendar size={32} className="mx-auto mb-2 opacity-50" />
                            <p className="text-sm">No upcoming life events</p>
                            <p className="text-xs mt-1">Add financial goals to see personalized milestones</p>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3 flex-1 overflow-y-auto">
                        {events.map((event) => (
                            <div
                                key={event.event_id}
                                className={`border-l-4 ${getEventColor(event.event_type)} bg-slate-800/60 backdrop-blur-sm rounded-r-lg p-3 transition-all hover:bg-slate-700/60`}
                            >
                                <div className="flex items-start gap-3">
                                    <div className="mt-1">
                                        {getEventIcon(event.event_type)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-2 mb-1">
                                            <h3 className="text-sm font-semibold text-slate-100 leading-tight">
                                                {event.title}
                                            </h3>
                                            <span className="text-xs text-slate-400 whitespace-nowrap">
                                                {formatDate(event.date)}
                                            </span>
                                        </div>
                                        <p className="text-xs text-slate-300 mb-2 leading-relaxed">
                                            {event.impact}
                                        </p>
                                        <div className="flex items-center justify-between">
                                            <p className="text-xs text-primary text-center font-medium transition-colors">
                                                {event.recommended_action}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default LifeEventsWidget;
